/**
 * Die Reiter des Bestell-Drawers und die Rechte, die je Reiter das SCHREIBEN freischalten.
 *
 * Alle drei Konstanten sind woertlich aus `index.html:8356-8366` uebernommen - Reihenfolge,
 * Beschriftung, Symbolnamen und Rechtenamen unveraendert.
 *
 * ================= Der Satz, den dieses Modul traegt =================
 *
 * LESEN steht allen offen. `DRAWER_TAB_EDIT_PERMISSIONS` entscheidet NICHT, ob ein Reiter
 * erreichbar ist, sondern nur, ob die Bedienelemente darin bedienbar sind. Im Original ist das
 * daran zu sehen, dass `gotoTab` (`index.html:8377`) KEINE Rechtepruefung kennt - es setzt
 * `drawerTab` und zeichnet neu. Erst `openDrawer` (`index.html:9252`) bildet
 * `canEditTab = hasPermission(DRAWER_TAB_EDIT_PERMISSIONS[drawerTab])` und schaltet damit am Ende
 * die Bedienelemente scharf oder stumpf. Ein Nachbau, der die Reiter selbst an das Schreibrecht
 * bindet, sperrt Leser aus einer Ansicht aus, die ihnen im Bestand offensteht.
 */

/**
 * `index.html:8356-8362`, ERGAENZT um den Reiter `ship` ("Spedition") - kein Bestandsreiter, siehe
 * unten.
 *
 * Auftraggeber, woertlich (2026-08-20): „kannst du hier ein neues reiter einfuegen fuer die
 * speditionsdaten … die sind aktuell bei bestelldaten mit drin … die dsv verknuepfung findet
 * zukuenftig ueber ein drop down / Suchfeld statt und wir zeigen hier noch die tracking daten dann
 * mit an von dsv." `ship` steht bewusst AN ZWEITER STELLE, direkt hinter `main`: die Felder ziehen
 * aus dessen Abschnitt `main-data` um, der neue Reiter gehoert fachlich dorthin. Ausfuehrlich:
 * `docs/superpowers/decisions/2026-08-20-reiter-spedition.md`.
 *
 * ERGAENZT ausserdem um den Reiter `history` ("Historie", Auftrag "Bestellungs-Aenderungshistorie",
 * Task 11 des dortigen Plans - nicht zu verwechseln mit dem gleichnamigen Task des SPA-
 * Verdrahtungsplans, siehe dessen Spuren weiter unten in dieser Datei): der vereinheitlichte
 * Zeitstrahl aus Task 10 (`GET /api/orders/:id/history`) - ebenfalls kein Bestandsreiter, an LETZTER
 * Stelle angehaengt statt zwischen bestehende Reiter eingeschoben, damit kein bereits eingespieltes
 * Baseline-Bild (Reihenfolge der `.dtab`-Knoten) verschiebt.
 */
export const DRAWER_TABS = [
  { id: "main", label: "Bestellung" },
  { id: "ship", label: "Spedition" },
  { id: "arrival", label: "Anlieferung" },
  { id: "tasks", label: "Tasks" },
  { id: "docs", label: "Dokumente" },
  { id: "pay", label: "Zahlung" },
  { id: "calc", label: "EK" },
  { id: "history", label: "Historie" },
] as const;

export type DrawerTabId = (typeof DRAWER_TABS)[number]["id"];

/**
 * `index.html:8363`, ERGAENZT um `ship: "truck"` (siehe `DRAWER_TABS`) und `history: "history"`
 * (Task 11) - `History` steht in `Ikone.tsx#KOMPONENTEN` bereits als `history: History` (dort schon
 * fuer den Dokumente-Reiter im Einsatz, "Verlauf"), kein neues Symbol noetig.
 */
export const DRAWER_TAB_ICONS: Record<DrawerTabId, string> = {
  main: "clipboard-list",
  ship: "truck",
  arrival: "package-check",
  tasks: "list-checks",
  docs: "files",
  pay: "credit-card",
  calc: "calculator",
  history: "history",
};

/**
 * `index.html:8366`. Der Kommentar dort, woertlich: "Der Zahlungs-Tab ist der einzige, den sich
 * zwei Rollen teilen: 'Standard' stoesst die Zahlung an, 'Zahlungsansicht' fuehrt sie aus. Die
 * feinere Trennung steckt in data-permission am Bedienelement."
 *
 * `pay` traegt deshalb eine LISTE. `hatRecht` verlangt davon nur EINES (siehe unten) - ein Nachbau,
 * der beide verlangt, nimmt der Rolle "Standard" den Knopf "Zahlung anstossen".
 *
 * `history` (Reiter "Historie", Task 11) traegt ausdruecklich `undefined`, KEIN `Partial<Record<...>>`
 * (Nachbesserung, Pruefer-Befund: eine Typlockerung haette JEDEM kuenftigen Reiter die Pflicht
 * genommen, hier ueberhaupt einen Eintrag zu haben - ein vergessener Eintrag waere durchkompiliert).
 * Der Zeitstrahl ist REINE ANZEIGE ohne ein einziges Bedienelement - anders als bei `arrival` oder
 * `tasks` (siehe deren Kommentare unten) gibt es hier nicht einmal eine Leseaktion, die ein eigenes
 * `data-permission` traegt. Ein ECHTES Recht waere entweder ERFUNDEN (kein Recht deckt "diesen
 * Reiter bearbeiten", weil es nichts zu bearbeiten gibt) oder IRREFUEHREND (ein bestehendes Recht wie
 * `edit_orders` haette hier keinerlei Wirkung, taeuschte aber eine vor). `darfTabBearbeiten` liefert
 * fuer `undefined` ueber `hatRecht(rechte, undefined)` zuverlaessig `false` - siehe dessen Rumpf:
 * `[undefined].flat().some(item => item !== undefined && ...)` ist immer leer.
 */
export const DRAWER_TAB_EDIT_PERMISSIONS: Record<DrawerTabId, string | string[] | undefined> = {
  main: "edit_orders",
  // Kein Bestandswert (`ship` existiert dort nicht) - dasselbe Recht wie `main`, denn die Felder
  // ziehen nur zwischen zwei Abschnitten DESSELBEN Reiters um (`main-data` -> `ship`), sie bekommen
  // dabei keine neue fachliche Zustaendigkeit.
  ship: "edit_orders",
  // Ebenfalls kein Bestandswert: der Reiter zeigt nur an (Positions- und Einbuchungsliste), es gibt
  // kein Schreibformular - analog zur Begruendung bei `ship`. Der EINZIGE Bedienweg des Reiters (der
  // Aufklapp-Schalter der Positionszeile) traegt seit Fix Stufe 2 (Important #1, 2026-08-26) ein
  // eigenes `data-permission="view_board"` an seiner Tabelle - dieselbe Ausnahme wie `request_payment`
  // am Anstoss-Knopf von `pay` (siehe Kommentar dort): eine reine Leseaktion darf nicht an
  // `edit_orders` haengen, das Constraint 9 ("Lesen steht allen Rollen offen") verletzen wuerde.
  arrival: "edit_orders",
  tasks: "manage_tasks",
  docs: "manage_documents",
  pay: ["manage_payments", "request_payment"],
  calc: "manage_costs",
  // Siehe Kopfkommentar oben: ausdruecklich `undefined`, nicht weggelassen - der `Record`-Typ
  // verlangt den Schluessel weiterhin, der Wert druckt "keine Berechtigung noetig" aus.
  history: undefined,
};

/**
 * `index.html:5302`, woertlich:
 * `return [permission].flat().some(item => CURRENT_PERMISSIONS.has(item));`
 *
 * Der Rechtesatz kommt hier als Parameter herein statt aus einem Modul-Global
 * (`CURRENT_PERMISSIONS`), weil die SPA ihn im Store fuehrt (`Speicher`, Feld `rechte`).
 * Die Entscheidungsregel selbst ist unveraendert: `[x].flat()` macht aus einem Einzelwert eine
 * einelementige Liste, `some` laesst EINES der genannten Rechte genuegen.
 */
export function hatRecht(
  rechte: ReadonlySet<string>,
  permission: string | string[] | undefined,
): boolean {
  return [permission].flat().some((item) => item !== undefined && rechte.has(item));
}

/** Das Schreibrecht des gerade offenen Reiters - `canEditTab` aus `index.html:9252`. */
export function darfTabBearbeiten(rechte: ReadonlySet<string>, tab: DrawerTabId): boolean {
  return hatRecht(rechte, DRAWER_TAB_EDIT_PERMISSIONS[tab]);
}

/**
 * `index.html:9302` - der Hinweis, den ein gesperrtes Bedienelement traegt.
 *
 * ZWEI VERBRAUCHER, deshalb hier und nicht mehr in `Drawer.tsx` (Fix-Runde 1 zu Task 11,
 * 2026-08-11): der Rechtedurchgang des Drawers setzt ihn (`Drawer.tsx`), und
 * `Suppliers/ExtrasFormular.tsx` sperrt seine Felder mit GENAU demselben Wortlaut (Entscheidung des
 * Auftraggebers vom 2026-08-11, "ausgegraut plus Tooltip").
 *
 * Task 11 hatte ihn dafuer aus `Drawer.tsx` exportiert - der Satz stand damit an EINER Stelle, aber
 * das Lieferantenformular zog dafuer das ganze Drawer-Modul herein (React-Zustand, DOM-Durchgang,
 * Breitenrechnung), um an eine Zeichenkette zu kommen. Dieses Modul ist die schlankere Naht: es
 * fuehrt bereits das gesamte Rechtevokabular des Drawers, haengt an nichts und wird ohnehin von
 * `App.tsx` gelesen.
 *
 * EINE Quelle bleibt der Punkt: eine zweite Abschrift waere die naechste Stelle, an der ein
 * Bindestrich still zum Halbgeviertstrich wird - so wie es
 * `App.tsx#ANLAGEMASKE_VERWORFENE_FELDER` fuer die Erfolgsmeldung der Anlagemaske loest.
 */
export const NUR_LESEN_TITEL = "Nur Lesen – für Änderungen fehlt die Rollenberechtigung";
